import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { Dispatch, ReactElement, SetStateAction } from "react";

import { ApiError } from "../lib/auth";
import { isLimitReached } from "../lib/usageApi";
import { evaluateCompCriteria, getCompSearchDefaults, resolveCascade } from "./api";
import { cascadeMeta, compsFromCascade, heldRowsFromCascade, type CascadeMeta, type CascadeResolveArgs } from "./cascade";
import { isClosedSale } from "./constants";
import { normalizeAddress } from "./AddressMismatchPrompt";
import { buildMapPoints } from "./compMapModel";
import { applyHeld, matchedCount, mergePool, unknownLotCount, verdictFor, widenWithin, type HeldSubject } from "./compHeldFilter";
import { CompSearchMap } from "./CompSearchMap";
import { AcreageNotice } from "./AcreageNotice";
import {
  criteriaToRequest, describeCriteria, flagOutside, isSearchResult, normalizeCriteria, ownSourceCount,
  ACREAGE_RADIUS_MAX_MI, LOT_OFF_CONDO_REASON, reconcileRerun, sameCriteria, sourceCountLine, startingCriteria, startingLotOnly, typeUnstatedLine, uncheckedLine,
  visibleSources,
  type CompCriteria, type CompSearchDefaults, type CompSearchSummary, type CompSourceCount, type CompStatus,
  type HomeType, type SubjectHomeType,
} from "./compCriteria";
import type { CandidateComp } from "./types";
import { WIDEN_CHIPS_BELOW, widenChips, widenedSentence } from "./widenChips";

/**
 * FIND COMPARABLE SALES (fan-out 1G) — the search panel and the map on the comps step.
 *
 * HOW TO OPEN IT FROM ELSEWHERE. This component is controlled: the screen that renders it owns
 * `open` / `onOpenChange`. Anything that needs the panel (the no-comps card's "Adjust search")
 * calls `onOpenChange(true)` on the same state; the panel then moves focus to its first field. In
 * `CmaAmbFlow` that state is `compSearchOpen` / `setCompSearchOpen`; the no-comps card's
 * [Adjust search] is `onClick={() => setCompSearchOpen(true)}`.
 *
 * RULES THIS FILE HOLDS
 *   * Editing a field never ASKS A SOURCE. It re-filters, instantly and in the browser, the sales
 *     the last search already fetched (the kept ones plus `heldBack`, the rows the same pull set
 *     aside: `compHeldFilter.ts`). One button, "Refresh from MLS" (plain "Search again" when the
 *     account has no MLS), asks the sources again — every request can spend a provider allowance,
 *     so that cost is always the agent's explicit choice. If fewer than 12 options show, the
 *     browser widens within what it holds (living area first, then distance) and says exactly what
 *     moved. The held set lives in this component's memory for the working session only.
 *   * The defaults are the server's (the AMB comp-pull constants), fetched, never spelled here.
 *   * A control that cannot act says why in the same place: Active / Pending without MLS, the
 *     living-area and year bands when the home's figures are not on record, same subdivision when
 *     the subdivision is not stated. No dead control.
 *   * Nothing the agent holds is lost: ticked sales survive a re-run (flagged if the new search
 *     would not have found them) and uploaded sales outside the criteria are flagged, not removed.
 *   * An outage is never reported as "none found": a source that could not be asked says so.
 */

export interface SubjectFacts {
  address: string;
  /** Locality-qualified address for geocoding the centre of the circle. */
  query: string;
  gla: number | null;
  /** Where the living area came from, in a person's words ("Collin CAD 2026", "MLS", "You
   * entered"), or null when the grid names no source. See `glaSourceWords`. */
  glaSource?: string | null;
  /** The subject's lot in square feet (R2) and where it came from, in words; null = not on record, so
   * the lot-size filter cannot apply and the control says so. */
  lot?: number | null;
  lotSource?: string | null;
  year: number | null;
  beds: number | null;
  subdivision: string | null;
}

const SOURCE_NAME: Record<string, string> = {
  mls: "MLS",
  cotality: "Public records",
  cad: "County records",
  files: "Uploaded comps",
  own: "Sales you added",
};
const OWN_LABEL = SOURCE_NAME.own;

/** The living area's source, in the words the panel prints: the county record's own tag, "MLS",
 * "public records", "your uploaded report" or "You entered". Never a vendor name. */
export function glaSourceWords(tag: string | null | undefined, edited = false): string | null {
  if (edited) return "You entered";
  const t = (tag ?? "").trim();
  if (!t) return null;
  if (t === "You entered") return t;
  if (/NTREIS|\bMLS\b/i.test(t)) return "MLS";
  if (/\bCAD\b/.test(t)) return t;
  if (/cotality|public.record/i.test(t)) return "public records";
  if (/upload/i.test(t)) return "your uploaded report";
  return t;
}

/** What a person reads for a source: never a vendor name from the payload. */
export function sourceDisplay(s: CompSourceCount): CompSourceCount {
  return { ...s, label: SOURCE_NAME[s.rung] ?? s.label };
}

const NO_ROWS: CandidateComp[] = [];

/** What the instant filter did on the last change: the counts and the sentences to print. */
interface LocalView {
  held: number;
  shown: number;
  used: CompCriteria;
  widenedNote: string | null;
  shortNote: string | null;
  /** Set when the controls are narrower than the pull the server actually made: says exactly how. */
  narrowedNote: string | null;
}
const rowKey = (c: CandidateComp) => normalizeAddress(c.name);

export function CompSearch({
  open,
  onOpenChange,
  subject,
  comps,
  onCompsChange,
  onListingsChange,
  onMeta,
  buildArgs,
  rungLabels,
  order,
  activeIndex,
  onActiveChange,
  onPinClick,
  mapLoader,
  cascadeSummary,
  activesLine,
  focusRequest,
  heldBack = NO_ROWS,
  actives = NO_ROWS,
}: {
  /** The MLS sales the last pull fetched that the criteria set aside (`held_back` of the cascade).
   * Memory only: rows a filter brings on screen carry `_held_mls`, and `draftStore.saveDraft` strips them from every saved copy. */
  heldBack?: CandidateComp[];
  /** The competing listings (Active and Pending). They get pins too, lettered A or P, never numbered. */
  actives?: CandidateComp[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: SubjectFacts;
  comps: CandidateComp[];
  onCompsChange: Dispatch<SetStateAction<CandidateComp[]>>;
  onListingsChange: Dispatch<SetStateAction<CandidateComp[]>>;
  /** The cascade's reporting after a re-run, so the notices, ladder and gap list stay in step. */
  onMeta: (meta: CascadeMeta) => void;
  /** The cascade request to re-ask (same address, lock and subject facts as the last one). */
  buildArgs: () => CascadeResolveArgs;
  rungLabels?: Record<string, string>;
  /** The grid's display order (indices into `comps`): the numbers on the pins. */
  order: number[];
  activeIndex: number | null;
  onActiveChange: (index: number | null) => void;
  onPinClick?: (index: number) => void;
  mapLoader?: Parameters<typeof CompSearchMap>[0]["loader"];
  /** The `comp_search` block of the cascade run that produced the current candidates (the first
   * run, before any "Search again"): its widen sentence and counts show without a click. */
  cascadeSummary?: CompSearchSummary | null;
  /** One sentence on the competing-homes search: the radius it ACTUALLY covered (its own, not the
   * sold search's) and what it found, or why it did not run. `activesSearchLine`. */
  activesLine?: string | null;
  /** Bumped by a caller (the no-comps card's [Adjust search]) to ask for focus and scroll even
   * when the panel is already open, where `open` alone would not change. 0 or omitted = no ask. */
  focusRequest?: number;
}): ReactElement {
  const [defaults, setDefaults] = useState<CompSearchDefaults | null>(null);
  const [defaultsFailed, setDefaultsFailed] = useState(false);
  const [draft, setDraft] = useState<CompCriteria | null>(null);
  const [applied, setApplied] = useState<CompCriteria | null>(null);
  const [summary, setSummary] = useState<CompSearchSummary | null>(null);
  const [ownCount, setOwnCount] = useState<CompSourceCount | null>(null);
  const [skippedNote, setSkippedNote] = useState<string[]>([]);
  const [keptOutside, setKeptOutside] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "Rural location" / "High-end luxury": set by the agent, never by us. They only change whether
  // "Any size" carries "not recommended"; they filter nothing. [ASSUMPTION, NEEDS INPUT]
  const [marks, setMarks] = useState<SearchMarks>({ rural: false, luxury: false });
  // C1: every widening the agent pressed, in order, as the sentence that says what moved (from -> to).
  // Cleared only by "Reset to standard": a widening stays stated as long as it is in force.
  const [widenings, setWidenings] = useState<string[]>([]);
  const firstField = useRef<HTMLInputElement>(null);
  const headingId = useId();
  const compsRef = useRef(comps);
  compsRef.current = comps;
  // THE HELD SET (comps-ux): every search result the last pull fetched, matched or not. Re-filtered
  // in the browser on every control change; replaced only by a refresh. Memory only.
  const [pool, setPool] = useState<CandidateComp[]>([]);
  const [local, setLocal] = useState<LocalView | null>(null);
  const summaryRef = useRef<CompSearchSummary | null>(null);
  const appliedRef = useRef<CompCriteria | null>(null);
  const subjectRef = useRef(subject);
  subjectRef.current = subject;
  summaryRef.current = summary ?? cascadeSummary ?? null;
  appliedRef.current = applied;
  const rungLabelsRef = useRef(rungLabels);
  rungLabelsRef.current = rungLabels;

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => getCompSearchDefaults())
      .then((d) => {
        if (cancelled || !d) return;
        setDefaults(d);
        // No automatic criteria on the summary (an older brief): a condo subject still starts with the lot range off (R6).
        setDraft((cur) => cur ?? startingLotOnly(normalizeCriteria(d.defaults), cascadeSummary?.subject_home_type));
      })
      .catch(() => { if (!cancelled) setDefaultsFailed(true); });
    return () => { cancelled = true; };
  }, []);

  // Opening the panel from elsewhere lands the keyboard on its first field.
  useEffect(() => {
    if (open && defaults) firstField.current?.focus();
  }, [open, defaults]);

  // A caller asking again while the panel is already open: bring it into view and refocus.
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!focusRequest || !open || !defaults) return;
    const el = firstField.current;
    el?.focus({ preventScroll: true });
    const target = sectionRef.current;
    if (target && typeof target.scrollIntoView === "function") target.scrollIntoView({ block: "start", behavior: "auto" });
  }, [focusRequest, open, defaults]);

  // THE AUTOMATIC SEARCH'S CRITERIA ARE THE PANEL'S STARTING POINT (2026-10-22). The first run
  // already searched ±200 sq ft and the subject's stated type; the options show exactly that, as
  // the search that ran, until the agent runs another.
  const autoKey = cascadeSummary?.auto && cascadeSummary.criteria ? JSON.stringify(cascadeSummary.criteria) : "";
  useEffect(() => {
    if (!autoKey || summary) return;
    const c = normalizeCriteria(JSON.parse(autoKey));
    setDraft(c);
    setApplied(c);
  }, [autoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The pool is seeded when the cascade's held-back rows arrive (first run, or a retry from the
  // outage panel): what is on screen from the search plus what the same pull set aside.
  useEffect(() => {
    const onScreen = compsRef.current.filter((c) => isSearchResult(c, rungLabelsRef.current));
    setPool(mergePool(onScreen, heldBack, rowKey));
    setLocal(null);
  }, [heldBack]);

  const { points, unplaced } = useMemo(() => buildMapPoints(comps, order, actives), [comps, order, actives]);

  const isOwn = (c: CandidateComp) => !isSearchResult(c, rungLabels);

  /** Re-filter the held set for these criteria: no request, no wait. See `compHeldFilter.ts`. */
  function applyLocal(next: CompCriteria, source: CandidateComp[] = pool) {
    if (source.length === 0) { setLocal(null); return; }
    const facts: HeldSubject = {
      gla: subjectRef.current.gla, lot: subjectRef.current.lot ?? null,
      year: subjectRef.current.year, subdivision: subjectRef.current.subdivision,
    };
    // START FROM WHAT THE SERVER ACTUALLY SEARCHED, widening included (criteria 0.5 mi / 9 months,
    // pull 1 mi / 12 months): a distance or months control the agent has not moved keeps the pull's
    // reach, so a change to another control never silently narrows it. A control the agent moved
    // is theirs, and when it is narrower than the pull the panel says so (`narrowedNote`).
    const pull = (summaryRef.current ?? null)?.scope ?? null;
    const base = appliedRef.current;
    const start: CompCriteria = { ...next };
    if (pull && base) {
      if (next.radius_mi === base.radius_mi) start.radius_mi = Math.max(next.radius_mi, pull.radius_mi);
      if (next.months === base.months) start.months = Math.max(next.months, pull.months);
    }
    let narrowedNote: string | null = null;
    if (pull && (start.radius_mi < pull.radius_mi - 1e-9 || start.months < pull.months)) {
      const far = start.radius_mi < pull.radius_mi - 1e-9;
      const old = start.months < pull.months;
      narrowedNote = `Showing sales within ${Number(start.radius_mi.toFixed(2))} mi and ${start.months} months. The last search returned sales up to ${Number(pull.radius_mi.toFixed(2))} mi and ${pull.months} months, so ${far && old ? "farther and older" : far ? "farther" : "older"} sales are held back here, not lost.`;
    }
    const res = widenWithin(source, start, facts);
    const isSearch = (c: CandidateComp) => isSearchResult(c, rungLabelsRef.current);
    const { comps: laid, keptOutside: kept } = applyHeld(compsRef.current, source, res.rows, isSearch, rowKey);
    // The rows the agent brought are judged by the same rule, here, with no server call.
    const judged = laid.map((c) => isSearch(c) ? c
      : (verdictFor(c, res.used, facts).matches ? { ...c, _outside_criteria: undefined } : { ...c, _outside_criteria: true }));
    onCompsChange(judged);
    setKeptOutside(kept);
    setLocal({ held: source.length, shown: matchedCount(res.rows), used: res.used,
               widenedNote: res.widenedNote, shortNote: res.shortNote, narrowedNote });
  }

  function change(next: CompCriteria) {
    setDraft(next);
    applyLocal(next);
  }

  async function search() {
    if (!draft || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { upload_pending: _u, file_comps: _f, agent_comps: _a, comp_params: _p, ...base } = buildArgs();
      const result = await resolveCascade({ ...base, want_comps: true, comp_criteria: criteriaToRequest(draft) });
      const incoming = compsFromCascade(result.comps).filter(isClosedSale);
      const key = (c: CandidateComp) => normalizeAddress(c.name);

      // The rows the agent brought: judged by the server's own filter, flagged, never removed.
      const own = compsRef.current.filter(isOwn);
      let verdicts: { matches: boolean; unchecked: string[] }[] = [];
      let skipped: string[] = [];
      if (own.length) {
        try {
          const ev = await evaluateCompCriteria({
            comp_criteria: criteriaToRequest(draft),
            subject: { gla: subject.gla, lot_sf: subject.lot ?? null, year: subject.year, beds: subject.beds, subdivision: subject.subdivision },
            rows: own,
          });
          verdicts = ev.verdicts;
          skipped = ev.skipped;
        } catch {
          verdicts = own.map(() => ({ matches: true, unchecked: [] })); // unjudged = not flagged
        }
      }
      const { comps: next, keptOutside: kept } = reconcileRerun(compsRef.current, incoming, rungLabels, key);
      onCompsChange(flagOutside(next, isOwn, verdicts));
      setKeptOutside(kept);
      // The new pull is the new held set: what it kept plus what its criteria set aside.
      setPool(mergePool(incoming, heldRowsFromCascade(result.held_back).filter(isClosedSale), rowKey));
      setLocal(null);

      // MLS competing listings: this search's set replaces the last MLS set; listings the agent
      // added another way are untouched.
      if (result.mls_actives) {
        const fresh = compsFromCascade(result.mls_actives);
        onListingsChange((ls) => {
          const mine = ls.filter((l) => l._source !== "NTREIS MLS");
          const have = new Set(mine.map((l) => key(l)));
          return [...mine, ...fresh.filter((l) => !have.has(key(l))).map((l, i) => ({ ...l, _selected: mine.length + i < 3 }))];
        });
      }
      onMeta(cascadeMeta(result));
      setSummary(result.comp_search ?? null);
      // The rows the agent brought (uploaded or typed) are one line, and only once the server judged them.
      setOwnCount(own.length && verdicts.length ? { ...ownSourceCount(OWN_LABEL, verdicts), rung: "own" } : null);
      setSkippedNote([...(result.comp_search?.skipped ?? []), ...skipped.filter((s) => !(result.comp_search?.skipped ?? []).includes(s))]);
      setApplied(draft);
    } catch (e) {
      if (isLimitReached(e)) setError(e.body.message);
      else {
        const why = e instanceof ApiError && e.status === 422 ? ` ${e.message}` : "";
        setError(`We couldn't reach the search just now.${why} Nothing has been ruled out, and the sales you already have are untouched. Try again.`);
      }
    } finally {
      setBusy(false);
    }
  }

  if (defaultsFailed) {
    return (
      <section ref={sectionRef} className="vb-cs" aria-labelledby={headingId}>
        <h2 id={headingId} className="vb-cs-h">Find comparable sales</h2>
        <p className="vb-cs-note">Search options aren&apos;t available right now. The sales below are unaffected.</p>
      </section>
    );
  }
  if (!defaults || !draft) {
    return (
      <section className="vb-cs" aria-labelledby={headingId} aria-busy="true">
        <h2 id={headingId} className="vb-cs-h">Find comparable sales</h2>
      </section>
    );
  }

  const shown = summary ?? cascadeSummary ?? null;
  const subjectType: SubjectHomeType | null = shown?.subject_home_type ?? cascadeSummary?.subject_home_type ?? null;
  const standard = startingCriteria(defaults.defaults, subjectType);
  const sources = [
    ...visibleSources(shown).filter((s) => s.rung !== "files"),
    ...(ownCount ? [ownCount] : visibleSources(shown).filter((s) => s.rung === "files" && s.found > 0)),
  ].map(sourceDisplay).filter((s) => s.rung === "cad" || s.status !== "not_available");
  // After the one automatic widen, the circle is the radius ACTUALLY searched (1 mi), not the
  // 0.5 mi the options still show, until the agent moves the slider (UX review: the map must not
  // contradict the widen sentence above it).
  const searchedRadius = shown?.widened && shown.scope ? shown.scope.radius_mi : null;
  const lastRadius = searchedRadius ?? applied?.radius_mi ?? defaults.defaults.radius_mi;
  const circleRadius = local
    ? local.used.radius_mi
    : searchedRadius !== null && sameCriteria(applied ?? defaults.defaults, draft)
      ? searchedRadius : draft.radius_mi;
  // The pull's own reach: a control set wider than this cannot find more in the browser.
  const pulled = shown?.scope ?? null;
  const beyondPull = !!local && !!pulled && (draft.radius_mi > pulled.radius_mi + 1e-9 || draft.months > pulled.months);
  const mlsOn = defaults.statuses.active.available;
  const refreshLabel = mlsOn ? "Refresh from MLS" : "Search again";
  // "N comps have no lot size on record": over every sale the panel holds (what the search returned,
  // plus the rows the agent brought), so the count stays true while the toggle hides them.
  const lotNoRecord = unknownLotCount(pool.length ? [...pool, ...comps.filter(isOwn)] : comps);
  const notes = [...sources.map(typeUnstatedLine), ...sources.map(uncheckedLine)].filter((x): x is string => !!x);
  // C1 WIDEN CHIPS: fewer than six sales pass -> one chip per criterion that can loosen one step.
  const passing = comps.filter((c) => !c._outside_criteria && !c._home_type_unstated && !c._lot_unmatched).length;
  const chips = passing < WIDEN_CHIPS_BELOW ? widenChips(draft, defaults, subject) : [];

  // SQUARE MAP BESIDE THE PANEL (2026-10-22). `.vb-cs-main` (heading, results, options) and
  // `.vb-cs-map` are the two columns at 1024 px and up; below that `.vb-cs-main` dissolves into the
  // one column (`display: contents`) so the map still sits above the options form on a phone.
  return (
    <section className="vb-cs" aria-labelledby={headingId} data-open={open || undefined}>
      <div className="vb-cs-body" data-open={open || undefined}>
      <div className="vb-cs-main">
      <header className="vb-cs-head">
        <div className="vb-cs-title">
          <h2 id={headingId} className="vb-cs-h">Find comparable sales</h2>
          <p className="vb-cs-summary" data-testid="comp-search-summary">
            {describeCriteria(applied ?? standard)}
          </p>
        </div>
        <button type="button" className="vb-cs-btn" aria-expanded={open} aria-controls="vb-cs-form"
                onClick={() => onOpenChange(!open)}>
          {open ? "Hide search options" : "Adjust search"}
        </button>
      </header>

      <div className="vb-cs-status" role="status" aria-live="polite">
        {shown?.widened_message && <p className="vb-cs-widened">{shown.widened_message}</p>}
        {!local && shown?.scope && shown.applied && !shown.widened && (
          <p className="vb-cs-note">Searched {Number(shown.scope.radius_mi.toFixed(2))} mi and {shown.scope.months} months.</p>
        )}
        {local && (
          <>
            <p className="vb-cs-note" data-testid="comp-search-held-count">
              Showing {local.shown} of {local.held} {local.held === 1 ? "sale" : "sales"} held from the last search.
            </p>
            {local.narrowedNote && <p className="vb-cs-note" data-testid="comp-search-narrowed">{local.narrowedNote}</p>}
            {local.widenedNote && <p className="vb-cs-widened" data-testid="comp-search-widened">{local.widenedNote}</p>}
            {local.shortNote && <p className="vb-cs-note" data-testid="comp-search-short">{local.shortNote}</p>}
            {beyondPull && pulled && (
              <p className="vb-cs-note" data-testid="comp-search-beyond">
                The last search covered {Number(pulled.radius_mi.toFixed(2))} mi and {pulled.months} months. Sales beyond that aren&apos;t held. Use &quot;{refreshLabel}&quot; to look further out.
              </p>
            )}
          </>
        )}
        {widenings.length > 0 && (
          <ul className="vb-cs-widenings" aria-label="What you widened" data-testid="comp-search-widenings">
            {widenings.map((w, i) => <li key={`${i}-${w}`} className="vb-cs-widened">{w}</li>)}
          </ul>
        )}
        {chips.length > 0 && (
          <div className="vb-cs-chips" data-testid="comp-search-widen-chips">
            <p className="vb-cs-note">
              {passing === 0 ? "No sales" : `Only ${passing} ${passing === 1 ? "sale" : "sales"}`} pass this search. Widen one thing at a time:
            </p>
            <ul className="vb-cs-chip-list">
              {chips.map((ch) => (
                <li key={ch.key}>
                  <button type="button" className="vb-cs-chip" disabled={busy}
                          aria-label={`Widen ${ch.what.toLowerCase()} from ${ch.from} to ${ch.to}`}
                          onClick={() => { setWidenings((w) => [...w, widenedSentence(ch)]); change(ch.next); }}>
                    {ch.what}: {ch.from} <span aria-hidden="true">→</span> {ch.to}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {activesLine && <p className="vb-cs-note" data-testid="actives-search-line">{activesLine}</p>}
        {!local && sources.length > 0 && (
          <ul className="vb-cs-counts" aria-label="Results by source">
            {sources.map((s) => <li key={s.rung} data-status={s.status}>{sourceCountLine(s)}</li>)}
          </ul>
        )}
        {/* The county's reason is a sentence, not a chip: a paragraph inside a pill wraps badly at 390. */}
        {sources.filter((s) => s.rung === "cad" && s.reason).map((s) => (
          <p key="cad-why" className="vb-cs-note">{s.reason}</p>
        ))}
        {sources.filter((s) => s.rung === "mls" && s.reason).map((s) => (
          <p key="mls-why" className="vb-cs-note">{s.reason}</p>
        ))}
        {shown?.acreage && <AcreageNotice acreage={shown.acreage} />}
        {!local && shown?.size_note && <p className="vb-cs-note" data-testid="comp-search-size-note">{shown.size_note}</p>}
        {!local && shown?.lot_note && <p className="vb-cs-note" data-testid="comp-search-lot-note">{shown.lot_note}</p>}
        {lotNoRecord > 0 && (
          <p className="vb-cs-note" data-testid="comp-search-lot-unknown">
            {lotNoRecord} {lotNoRecord === 1 ? "sale we found has" : "sales we found have"} no lot size on record.{draft.hide_unknown_lot ? " Hidden." : " Kept and marked."}
          </p>
        )}
        {subject.lot == null && draft.lot_band_pct != null && (
          <p className="vb-cs-note" data-testid="comp-search-lot-skipped">Lot-size range not applied: this home&apos;s lot size isn&apos;t on record.</p>
        )}
        {notes.map((n) => <p key={n} className="vb-cs-note">{n}</p>)}
        {skippedNote.map((n) => <p key={n} className="vb-cs-note">Not applied: {n}.</p>)}
        {keptOutside > 0 && (
          <p className="vb-cs-note">
            Kept {keptOutside} {keptOutside === 1 ? "sale" : "sales"} you had already selected that this search would not have found. {keptOutside === 1 ? "It's" : "They're"} marked.
          </p>
        )}
      </div>
      {error && <p className="vb-cs-error" role="alert">{error}</p>}

        {open && (
          <form id="vb-cs-form" className="vb-cs-form" onSubmit={(e) => { e.preventDefault(); void search(); }}>
            <CriteriaFields defaults={defaults} value={draft} onChange={change} subject={subject} firstRef={firstField}
                            subjectType={subjectType} marks={marks} onMarksChange={setMarks} />
            <div className="vb-cs-actions">
              <button type="submit" className="vb-cs-btn vb-cs-btn-search" disabled={busy} aria-busy={busy || undefined}>
                {busy ? "Searching…" : refreshLabel}
              </button>
              <button type="button" className="vb-cs-link" disabled={busy || sameCriteria(draft, standard)}
                      onClick={() => { setWidenings([]); change(standard); }}>
                Reset to standard
              </button>
              <span className="vb-cs-note" data-testid="comp-search-instant-note">
                {pool.length > 0
                  ? `Changes show right away from the ${pool.length} ${pool.length === 1 ? "sale" : "sales"} already held. ${mlsOn ? "Refresh from MLS" : "Search again"} asks the sources again.`
                  : `Changes apply when you ${mlsOn ? "refresh from MLS" : "search again"}: no sales are held yet to filter.`}
              </span>
            </div>
          </form>
        )}
      </div>
        <div className="vb-cs-map">
          <CompSearchMap
            points={points}
            unplaced={unplaced}
            subjectQuery={subject.query || null}
            radiusMi={circleRadius}
            appliedRadiusMi={lastRadius}
            activeIndex={activeIndex}
            onActiveChange={onActiveChange}
            onPinClick={onPinClick}
            loader={mapLoader}
          />
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------------------------
// The fields
// -------------------------------------------------------------------------------------------

export interface SearchMarks { rural: boolean; luxury: boolean }

const sqft = (n: number) => n.toLocaleString("en-US");
const YEAR_OPTIONS = [3, 5, 10, 15, 20, 30];
const BED_OPTIONS = [1, 2, 3, 4, 5];
const BATH_OPTIONS = [1, 1.5, 2, 2.5, 3, 3.5, 4];

export function CriteriaFields({
  defaults, value, onChange, subject, firstRef, subjectType = null,
  marks = { rural: false, luxury: false }, onMarksChange,
}: {
  defaults: CompSearchDefaults;
  value: CompCriteria;
  onChange: (c: CompCriteria) => void;
  subject: Pick<SubjectFacts, "gla" | "glaSource" | "lot" | "lotSource" | "year" | "subdivision">;
  firstRef?: React.RefObject<HTMLInputElement>;
  subjectType?: SubjectHomeType | null;
  marks?: SearchMarks;
  onMarksChange?: (m: SearchMarks) => void;
}): ReactElement {
  const set = (patch: Partial<CompCriteria>) => onChange({ ...value, ...patch });
  const r = defaults.ranges;
  const toggleStatus = (s: CompStatus, on: boolean) => {
    const next = new Set(value.statuses);
    if (on) next.add(s); else next.delete(s);
    next.add("sold");
    set({ statuses: (["sold", "active", "pending"] as CompStatus[]).filter((x) => next.has(x)) });
  };
  const noGla = subject.gla === null;
  const noLot = subject.lot == null;
  const toggleType = (t: HomeType, on: boolean) => {
    const next = new Set(value.home_types);
    if (on) next.add(t); else next.delete(t);
    set({ home_types: defaults.home_types.map((h) => h.value).filter((h) => next.has(h)) });
  };
  const anySizeOk = marks.rural || marks.luxury;
  const noYear = subject.year === null;
  const noSub = !subject.subdivision;
  const mls = defaults.statuses;

  return (
    <div className="vb-cs-fields">
      <div className="vb-cs-field">
        <div className="vb-cs-field-row">
          <label htmlFor="vb-cs-radius">Radius</label>
          <output htmlFor="vb-cs-radius" aria-hidden="true">{Number(value.radius_mi.toFixed(2))} mi</output>
        </div>
        <input id="vb-cs-radius" ref={firstRef} type="range" min={r.radius_mi.min} max={value.acreage_mode ? Math.max(r.radius_mi.max, ACREAGE_RADIUS_MAX_MI) : r.radius_mi.max} step={r.radius_mi.step ?? 0.25}
               value={value.radius_mi} aria-valuetext={`${Number(value.radius_mi.toFixed(2))} ${value.radius_mi === 1 ? "mile" : "miles"}`}
               onChange={(e) => set({ radius_mi: Number(e.target.value) })} />
      </div>
      <div className="vb-cs-field">
        <div className="vb-cs-field-row">
          <label htmlFor="vb-cs-months">Sold within</label>
          <output htmlFor="vb-cs-months" aria-hidden="true">{value.months} months</output>
        </div>
        <input id="vb-cs-months" type="range" min={r.months.min} max={r.months.max} step={r.months.step ?? 1}
               value={value.months} aria-valuetext={`${value.months} months`}
               onChange={(e) => set({ months: Number(e.target.value) })} />
      </div>

      <div className="vb-cs-field vb-cs-acreage-set">
        <label className="vb-cs-check">
          <input type="checkbox" checked={!!value.acreage_mode}
                 onChange={(e) => set(e.target.checked
                   ? { acreage_mode: true, lot_band_pct: null }
                   : { acreage_mode: undefined, site_acres_min: undefined, site_acres_max: undefined })} />
          <span>Acreage estate search</span>
        </label>
        {value.acreage_mode ? (
          <>
            <div className="vb-cs-field-pair">
              <div>
                <label htmlFor="vb-cs-acres-min">Smallest site (acres)</label>
                <input id="vb-cs-acres-min" type="number" min={0.1} step="any" value={value.site_acres_min ?? ""}
                       onChange={(e) => set({ site_acres_min: e.target.value === "" ? null : Number(e.target.value) })} />
              </div>
              <div>
                <label htmlFor="vb-cs-acres-max">Largest site (acres)</label>
                <input id="vb-cs-acres-max" type="number" min={0.1} step="any" value={value.site_acres_max ?? ""}
                       onChange={(e) => set({ site_acres_max: e.target.value === "" ? null : Number(e.target.value) })} />
              </div>
            </div>
            <p className="vb-cs-why" data-testid="comp-search-acreage-why">
              The site range replaces the lot-size range. Condo and complex lots are left out and counted. Changing the site range re-filters the sales already held right away. A sale that states no site is kept and marked &quot;site size not stated&quot;.
            </p>
          </>
        ) : (
          <p className="vb-cs-why">For homes on 2 acres or more. Turns on a wider radius, a longer window and a site-size range.</p>
        )}
      </div>

      <div className="vb-cs-field">
        <label htmlFor="vb-cs-gla">Living area</label>
        {noGla ? (
          /* NOT A SILENT "ANY SIZE" (2026-10-22): with no subject living area there is nothing to
             measure a range against, and the control says so where it would have been. */
          <select id="vb-cs-gla" disabled aria-describedby="vb-cs-gla-why" value="unknown">
            <option value="unknown">Living area unknown — size filter can&apos;t apply</option>
          </select>
        ) : (
          <select id="vb-cs-gla" aria-describedby="vb-cs-gla-src"
                  value={value.gla_band_sqft ?? ""}
                  onChange={(e) => set({ gla_band_sqft: e.target.value === "" ? null : Number(e.target.value), gla_band_pct: null })}>
            {defaults.gla_band_sqft_options.map((n) => (
              <option key={n} value={n}>within ±{sqft(n)} sq ft of this home ({sqft(subject.gla as number)} sq ft)</option>
            ))}
            <option value="">{anySizeOk ? "Any" : "Any — not recommended"}</option>
          </select>
        )}
        {noGla
          ? <p id="vb-cs-gla-why" className="vb-cs-why">This home&apos;s living area isn&apos;t on record yet. Add it on the previous step to search by size.</p>
          : (
            <>
              {/* THE RANGE, IN SQUARE FEET, FROM THE SUBJECT'S OWN LIVING AREA (comps-ux item 1). */}
              <p className="vb-cs-range" data-testid="comp-search-gla-range">
                {value.gla_band_sqft == null
                  ? "No limit on living area."
                  : `Showing ${sqft(Math.max(0, (subject.gla as number) - value.gla_band_sqft))}–${sqft((subject.gla as number) + value.gla_band_sqft)} sq ft.`}
              </p>
              <p id="vb-cs-gla-src" className="vb-cs-why">This home: {sqft(subject.gla as number)} sq ft{subject.glaSource ? `, from ${subject.glaSource}` : ""}.</p>
            </>
          )}
        <div className="vb-cs-marks">
          <label className="vb-cs-check">
            <input type="checkbox" checked={marks.rural} disabled={!onMarksChange}
                   onChange={(e) => onMarksChange?.({ ...marks, rural: e.target.checked })} />
            <span>Rural location</span>
          </label>
          <label className="vb-cs-check">
            <input type="checkbox" checked={marks.luxury} disabled={!onMarksChange}
                   onChange={(e) => onMarksChange?.({ ...marks, luxury: e.target.checked })} />
            <span>High-end luxury</span>
          </label>
          <p className="vb-cs-why">Size matters less for rural and high-end homes, so &quot;Any size&quot; isn&apos;t flagged for them. You set these; ValuBrief doesn&apos;t.</p>
        </div>
      </div>

      <div className="vb-cs-field">
        <label htmlFor="vb-cs-lot">Lot size</label>
        {noLot ? (
          /* NOT A SILENT "ANY LOT" (R6): with no subject lot there is nothing to measure against. */
          <select id="vb-cs-lot" disabled aria-describedby="vb-cs-lot-why" value="unknown">
            <option value="unknown">Lot size unknown — lot filter can&apos;t apply</option>
          </select>
        ) : (
          <select id="vb-cs-lot" aria-describedby="vb-cs-lot-src"
                  value={value.lot_band_pct ?? ""}
                  onChange={(e) => set({ lot_band_pct: e.target.value === "" ? null : Number(e.target.value) })}>
            {(defaults.lot_band_pct_options ?? [10, 15, 20, 25, 50]).map((n) => (
              <option key={n} value={n}>within ±{n}% of this home ({sqft(subject.lot as number)} sq ft)</option>
            ))}
            <option value="">Any</option>
          </select>
        )}
        {subjectType?.value === "condo" && value.lot_band_pct == null && (
          <p className="vb-cs-why" data-testid="comp-search-lot-off-condo">{LOT_OFF_CONDO_REASON}</p>
        )}
        {noLot
          ? <p id="vb-cs-lot-why" className="vb-cs-why">This home&apos;s lot size isn&apos;t on record yet. Add it on the previous step to search by lot.</p>
          : (
            <>
              <p className="vb-cs-range" data-testid="comp-search-lot-range">
                {value.lot_band_pct == null
                  ? "No limit on lot size."
                  : `Showing ${sqft(Math.round((subject.lot as number) * (1 - value.lot_band_pct / 100)))}–${sqft(Math.round((subject.lot as number) * (1 + value.lot_band_pct / 100)))} sq ft.`}
              </p>
              <p id="vb-cs-lot-src" className="vb-cs-why">This home: {sqft(subject.lot as number)} sq ft{subject.lotSource ? `, from ${subject.lotSource}` : ""}.</p>
            </>
          )}
        <label className="vb-cs-check">
          <input type="checkbox" checked={!!value.hide_unknown_lot}
                 onChange={(e) => set({ hide_unknown_lot: e.target.checked })} />
          <span>Hide unknown lot size</span>
        </label>
        <p className="vb-cs-why">Comps with no lot size on record are kept and marked unless you hide them.</p>
      </div>

      <fieldset className="vb-cs-field vb-cs-type-set" aria-describedby="vb-cs-type-src">
        <legend>Property type</legend>
        {defaults.home_types.map((h) => (
          <label key={h.value} className="vb-cs-check">
            <input type="checkbox" checked={value.home_types.includes(h.value)}
                   onChange={(e) => toggleType(h.value, e.target.checked)} />
            <span>{h.label}</span>
          </label>
        ))}
        <p id="vb-cs-type-src" className="vb-cs-why">
          {subjectType?.value
            ? `This home: ${subjectType.label}, from ${subjectType.source}.`
            : `${subjectType?.reason ?? "The records we have for this home don't state its property type."} Choose the types to include, or leave all three clear to search every type.`}
        </p>
        {value.home_types.length > 0 && (
          <p className="vb-cs-why">Sales that don&apos;t state a type are listed separately, not counted as matches.</p>
        )}
      </fieldset>
      <div className="vb-cs-field">
        <label htmlFor="vb-cs-year">Year built</label>
        <select id="vb-cs-year" disabled={noYear} aria-describedby={noYear ? "vb-cs-year-why" : undefined}
                value={value.year_band_years ?? ""} onChange={(e) => set({ year_band_years: e.target.value === "" ? null : Number(e.target.value) })}>
          <option value="">Any year</option>
          {YEAR_OPTIONS.map((y) => <option key={y} value={y}>within {y} years of this home</option>)}
        </select>
        {noYear && <p id="vb-cs-year-why" className="vb-cs-why">This home&apos;s year built isn&apos;t on record yet. Add it on the previous step to search by age.</p>}
      </div>
      <div className="vb-cs-field vb-cs-field-pair">
        <div>
          <label htmlFor="vb-cs-beds">Bedrooms</label>
          <select id="vb-cs-beds" value={value.min_beds ?? ""} onChange={(e) => set({ min_beds: e.target.value === "" ? null : Number(e.target.value) })}>
            <option value="">Any</option>
            {BED_OPTIONS.map((n) => <option key={n} value={n}>{n}+</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="vb-cs-baths">Bathrooms</label>
          <select id="vb-cs-baths" value={value.min_baths ?? ""} onChange={(e) => set({ min_baths: e.target.value === "" ? null : Number(e.target.value) })}>
            <option value="">Any</option>
            {BATH_OPTIONS.map((n) => <option key={n} value={n}>{n}+</option>)}
          </select>
        </div>
      </div>

      <div className="vb-cs-field">
        <label className="vb-cs-check">
          <input type="checkbox" checked={value.same_subdivision && !noSub} disabled={noSub}
                 aria-describedby={noSub ? "vb-cs-sub-why" : undefined}
                 onChange={(e) => set({ same_subdivision: e.target.checked })} />
          <span>Same subdivision{subject.subdivision ? ` (${subject.subdivision})` : ""}</span>
        </label>
        {noSub && <p id="vb-cs-sub-why" className="vb-cs-why">This home&apos;s subdivision isn&apos;t on record yet.</p>}
      </div>

      <fieldset className="vb-cs-field vb-cs-status-set">
        <legend>Status</legend>
        <label className="vb-cs-check">
          <input type="checkbox" checked disabled aria-describedby="vb-cs-sold-why" />
          <span>Sold</span>
        </label>
        <label className="vb-cs-check">
          <input type="checkbox" checked={value.statuses.includes("active") && mls.active.available} disabled={!mls.active.available}
                 aria-describedby={!mls.active.available ? "vb-cs-mls-why" : undefined}
                 onChange={(e) => toggleStatus("active", e.target.checked)} />
          <span>Active</span>
        </label>
        <label className="vb-cs-check">
          <input type="checkbox" checked={value.statuses.includes("pending") && mls.pending.available} disabled={!mls.pending.available}
                 aria-describedby={!mls.pending.available ? "vb-cs-mls-why" : undefined}
                 onChange={(e) => toggleStatus("pending", e.target.checked)} />
          <span>Pending</span>
        </label>
        <p id="vb-cs-sold-why" className="vb-cs-why">{mls.sold.reason}</p>
        {(!mls.active.available || !mls.pending.available) && (
          <p id="vb-cs-mls-why" className="vb-cs-why">{mls.active.reason ?? mls.pending.reason}</p>
        )}
        {mls.active.available && (
          <p className="vb-cs-why">Active and pending homes show as competition. They never price the report. Status changes apply when you refresh from MLS.</p>
        )}
      </fieldset>
    </div>
  );
}
